/*
 * "Great deals on add-ons" block on the product page.
 *
 * Two jobs, both of which Liquid cannot do on its own:
 *
 * 1. Clamp the teaser to a whole number of product rows. The shared teaser
 *    (bajaao-teaser.js) clamps to a count of text lines, which lands in the
 *    middle of a row when the content is images and prices, so this measures a
 *    real row and feeds the height back into the same CSS variables.
 *
 * 2. Strike through what the add-on costs on its own. A must-have-accessory
 *    product is a bundle-only twin of a real product - VAU-FURY15-MHA next to
 *    VAU-FURY15 - carrying its own, lower, compare-at price, so the saving it
 *    shows is not the saving against buying the thing separately. The handles
 *    do not match (word order differs) and Liquid cannot look a product up by
 *    SKU, so the twin is resolved here: search the base SKU, confirm a
 *    candidate really carries it, then show that product's MRP.
 *
 * Progressive: a row that cannot be resolved keeps the compare-at price Liquid
 * already rendered. The rows are not links - clicking one ticks its checkbox -
 * though this same lookup would give the standalone product's URL if that
 * changes.
 */
(function () {
  'use strict';

  var MHA_HANDLE = /-must-have-accessories$/i;
  var CACHE_PREFIX = 'bjaddons:';
  // The search ranks the exact SKU first; anything past a handful of
  // candidates is noise and not worth another round trip.
  var MAX_CANDIDATES = 4;

  /* ---------------------------------------------------------------- sizing */

  function sizeRows(block) {
    var body = block.querySelector('[data-bjt-body]');
    if (!body) return;

    var wanted = parseInt(block.getAttribute('data-bjt-rows'), 10) || 2;
    var rows = body.querySelectorAll('.product-bundle__info');
    if (rows.length <= wanted) return;

    var last = rows[wanted - 1];
    var height = last.getBoundingClientRect().bottom - body.getBoundingClientRect().top;
    if (height <= 0) return;

    // One "line" of exactly the height we measured, so the shared clamp
    // (lines x line-height) cuts on the row boundary. Writing the same value
    // back would re-trigger the ResizeObserver below for nothing.
    var clamp = Math.round(height) + 'px';
    if (block.style.getPropertyValue('--bjt-line-height') === clamp) return;

    block.style.setProperty('--bjt-lines', '1');
    block.style.setProperty('--bjt-line-height', clamp);
  }

  // bajaao-teaser.js re-measures every teaser on resize, which is also what
  // needs to happen once the clamp height changes underneath it.
  var queued = false;
  function remeasure() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      window.dispatchEvent(new Event('resize'));
    });
  }

  /* ------------------------------------------------------------ MHA lookup */

  function money(paise) {
    var value = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(paise / 100);
    return '₹ ' + value;
  }

  function cached(sku) {
    try {
      var hit = sessionStorage.getItem(CACHE_PREFIX + sku);
      return hit === null ? undefined : JSON.parse(hit);
    } catch (e) {
      return undefined;
    }
  }

  function remember(sku, match) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + sku, JSON.stringify(match));
    } catch (e) {
      /* private mode, full quota - the lookup just runs again next page */
    }
  }

  function getJSON(url) {
    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .catch(function () { return null; });
  }

  function variantWithSku(product, sku) {
    var wanted = sku.toUpperCase();
    for (var i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i];
      if ((variant.sku || '').toUpperCase() === wanted) return variant;
    }
    return null;
  }

  function findStandalone(sku) {
    var hit = cached(sku);
    if (hit !== undefined) return Promise.resolve(hit);

    var search = '/search/suggest.json?q=' + encodeURIComponent(sku) +
      '&resources[type]=product&resources[limit]=6' +
      '&resources[options][unavailable_products]=last';

    return getJSON(search).then(function (data) {
      var results = data && data.resources && data.resources.results;
      var candidates = (results && results.products) || [];

      // The twin itself always matches the search; it is the one page we are
      // trying to move the shopper away from.
      candidates = candidates.filter(function (p) { return !MHA_HANDLE.test(p.handle); });
      candidates = candidates.slice(0, MAX_CANDIDATES);

      // Sequential on purpose: the first candidate is almost always the right
      // one, so firing every request in parallel would waste most of them.
      var chain = Promise.resolve(null);
      candidates.forEach(function (candidate) {
        chain = chain.then(function (found) {
          if (found) return found;
          return getJSON('/products/' + candidate.handle + '.js').then(function (product) {
            if (!product || !product.variants) return null;
            var variant = variantWithSku(product, sku);
            if (!variant) return null;
            return { price: variant.price, compare: variant.compare_at_price };
          });
        });
      });

      return chain;
    }).then(function (match) {
      remember(sku, match);
      return match;
    });
  }

  function applyMatch(row, match) {
    if (!match) return;

    var strike = row.querySelector('[data-bundle-compare]');
    var input = row.querySelector('.bundleproductinput');
    if (!strike) return;

    // The list price to strike: the standalone SKU's MRP, or its live price
    // when it carries no MRP of its own.
    var list = match.compare || match.price;
    var paid = input ? Math.round(parseFloat(input.getAttribute('data-bundleprice') || '0') * 100) : 0;
    if (!list || list <= paid) return;

    strike.textContent = money(list);
    strike.hidden = false;

    // Keep the bundle total's struck figure built from the same numbers the
    // rows show. Skipped if the shopper already ticked the box, because that
    // amount is part of the running total by then.
    if (input && !input.checked) {
      input.setAttribute('data-bundlecompareprice', Math.round(list / 100));
    }
  }

  function resolvePrices(block) {
    block.querySelectorAll('[data-addon-mha]').forEach(function (row) {
      if (row.dataset.addonResolved) return;
      row.dataset.addonResolved = '1';

      var sku = row.getAttribute('data-addon-mha');
      if (!sku) return;

      findStandalone(sku).then(function (match) { applyMatch(row, match); });
    });
  }

  /* ------------------------------------------------------------------ init */

  function setup(block) {
    sizeRows(block);
    remeasure();
    resolvePrices(block);

    var body = block.querySelector('[data-bjt-body]');
    if (!body) return;

    // Lazy images settle after first paint, and switching variant re-renders
    // the whole list, so both the measured height and the prices are re-done.
    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(function () {
        sizeRows(block);
        remeasure();
      }).observe(body);
    }

    if (typeof MutationObserver === 'function') {
      new MutationObserver(function () {
        sizeRows(block);
        remeasure();
        resolvePrices(block);
      }).observe(body, { childList: true, subtree: true });
    }
  }

  function init() {
    var blocks = document.querySelectorAll('.bjt--addons');
    if (!blocks.length) return;

    var start = function () { blocks.forEach(setup); };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(start, { timeout: 1500 });
    } else {
      start();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
